import { Editor, Transforms } from "slate";
import { ReactEditor, useSlate } from "slate-react";

import { IconsEnum } from "components/icon";
import { ToolbarIcon, ToolbarSplit } from "components/toolbar";
import ImagesIcon from "components/toolbar/ImagesIcon";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { VariableSelect } from "routes/settings/email-template/components/VariableSelect";
import { useAttachments } from "routes/tickets/ticket/message-box/richtext/AttachmentProvider";
import { IUploadingAttachment } from "routes/tickets/ticket/message-box/richtext/TicketAttachments";
import { uniqueId } from "utils/string";

import { IVariableSelectParams } from "./formatted-text-editor";
import { ToolbarContainer } from "./formatted-text-editor/styles";
import {
  RichTextEditorPluginProps,
  RichTextPluginProps,
  RichTextVariables,
  shortcutTooltip,
} from "./plugins/constants";
import { RichBlockFormatType, RichTextFormatType, isBlockActive, isMarkActive } from "./plugins/richText";

type IToolbarProps = RichTextEditorPluginProps &
  RichTextPluginProps & {
    variablesPluginOptions?: RichTextVariables[];
    insertCleanVariables?: boolean;
    handleVariableSelect: (editor: Editor) => (params: IVariableSelectParams) => void;
    onLinkInsert: React.EventHandler<React.MouseEvent>;
    setUploadingAttachments: React.Dispatch<React.SetStateAction<{ [key: string]: IUploadingAttachment }>>;
    uploadingAttachments: { [key: string]: IUploadingAttachment };
    disabled: boolean;
    toolbarWrap: boolean;
  };

const RichTextEditorToolbar: React.FC<IToolbarProps> = ({
  onLinkInsert,
  uploadingAttachments,
  setUploadingAttachments,
  variablesPluginOptions,
  insertCleanVariables,
  handleVariableSelect,
  toolbarWrap,
  disabled,
  linksPlugin,
  richTextPlugin,
  variablesPlugin,
  imagesPlugin,
  attachmentsPlugin,
  withBold,
  withItalic,
  withUnderline,
  withStrikethrough,
  withCode,
  withNumberedList,
  withBulletedList,
}) => {
  const editor = useSlate();
  const { openFileDialog, uploadAttachment } = useAttachments();

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const isActiveText = (format: RichTextFormatType) => isMarkActive(editor, format);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const isActiveBlock = (format: RichBlockFormatType) => isBlockActive(editor, format);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleTextFormat = (format: RichTextFormatType) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    if (!ReactEditor.isFocused(editor)) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.start(editor, [0]));
    }

    const isActive = isMarkActive(editor, format);
    if (isActive) {
      editor.removeMark(format);
    } else {
      editor.addMark(format, true);
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleBlockFormat = (format: RichBlockFormatType) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();

    if (!ReactEditor.isFocused(editor)) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.start(editor, [0]));
    }

    editor.formatBlock(format);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleAttachmentClick = () => {
    openFileDialog({
      multiple: true,
      onFileSelect: files => {
        const newAttachments = Array.from(files).reduce((partial, file, index) => {
          const localID = uniqueId();
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          partial[localID] = { localID, file, order: index };
          return partial;
        }, {});

        // @ts-expect-error TS(2345): Argument of type '({ localID, file }: { localID: a... Remove this comment to see the full error message
        Object.values(newAttachments).forEach(({ localID, file }) => {
          uploadAttachment(localID, file, ({ total, loaded }) => {
            // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            if (uploadAttachment[localID]) {
              // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
              uploadAttachment[localID].progress = (loaded / total) * 100.0;
            }
          });
        });

        setUploadingAttachments({
          ...newAttachments,
          ...uploadingAttachments,
        });
      },
    });
  };

  const mobile = false;
  const privateMode = false;

  return (
    <ToolbarContainer wrap={toolbarWrap}>
      {richTextPlugin && (
        <>
          {withBold && (
            <ToolbarIcon
              testSelector="format-bold"
              icon={IconsEnum.FormatBold}
              active={isActiveText("bold")}
              disabled={disabled}
              tooltip={shortcutTooltip("mod+B", "Bold")}
              privateMode={privateMode}
              mobile={mobile}
              onMouseDown={handleTextFormat("bold")}
            />
          )}
          {withItalic && (
            <ToolbarIcon
              testSelector="format-italic"
              icon={IconsEnum.FormatItalic}
              active={isActiveText("italic")}
              disabled={disabled}
              tooltip={shortcutTooltip("mod+I", "Italic")}
              privateMode={privateMode}
              mobile={mobile}
              onMouseDown={handleTextFormat("italic")}
            />
          )}
          {withUnderline && (
            <ToolbarIcon
              testSelector="format-underline"
              icon={IconsEnum.FormatUnderline}
              tooltip={shortcutTooltip("mod+U", "Underline")}
              active={isActiveText("underline")}
              disabled={disabled}
              privateMode={privateMode}
              mobile={mobile}
              onMouseDown={handleTextFormat("underline")}
            />
          )}
          {withStrikethrough && (
            <ToolbarIcon
              testSelector="format-strikethrough"
              icon={IconsEnum.FormatStrikethrough}
              active={isActiveText("strikethrough")}
              tooltip={shortcutTooltip("mod+Shift+X", "Strikethrough")}
              disabled={disabled}
              privateMode={privateMode}
              mobile={mobile}
              onMouseDown={handleTextFormat("strikethrough")}
            />
          )}
          {withCode && (
            <ToolbarIcon
              testSelector="format-code"
              icon={IconsEnum.Code}
              active={isActiveText("code")}
              tooltip={shortcutTooltip("mod+Shift+C", "Code")}
              onMouseDown={handleTextFormat("code")}
              privateMode={privateMode}
              mobile={mobile}
              disabled={disabled}
            />
          )}
          {(withNumberedList || withBulletedList) && <ToolbarSplit privateMode={privateMode} />}
          {withNumberedList && (
            <ToolbarIcon
              testSelector="format-numbered-list"
              icon={IconsEnum.FormatListNumbered}
              active={isActiveBlock("numbered-list")}
              tooltip={shortcutTooltip("mod+Shift+8", "Numbered List")}
              disabled={disabled}
              privateMode={privateMode}
              mobile={mobile}
              onMouseDown={handleBlockFormat("numbered-list")}
            />
          )}
          {withBulletedList && (
            <ToolbarIcon
              testSelector="format-bulleted-list"
              icon={IconsEnum.FormatListBulleted}
              active={isActiveBlock("bulleted-list")}
              disabled={disabled}
              tooltip={shortcutTooltip("mod+Shift+7", "Bulleted List")}
              privateMode={privateMode}
              mobile={mobile}
              onMouseDown={handleBlockFormat("bulleted-list")}
            />
          )}
        </>
      )}
      {(linksPlugin || imagesPlugin || attachmentsPlugin) && <ToolbarSplit privateMode={privateMode} />}
      {linksPlugin && (
        <ToolbarIcon
          testSelector="insert-link"
          icon={IconsEnum.Link}
          active={false}
          disabled={disabled}
          tooltip={shortcutTooltip("mod+K", "Link")}
          privateMode={privateMode}
          mobile={mobile}
          onMouseDown={onLinkInsert}
        />
      )}
      {imagesPlugin && (
        <ImagesIcon mobile={mobile} privateMode={privateMode} disabled={disabled} tooltip="Insert Image" />
      )}
      {attachmentsPlugin && (
        <ToolbarIcon
          testSelector="attachments-button"
          icon={IconsEnum.Attachment}
          tooltip="Attachment"
          disabled={disabled}
          privateMode={privateMode}
          mobile={mobile}
          onMouseDown={handleAttachmentClick}
        />
      )}
      {variablesPlugin && (
        <VariableSelect
          onSelect={handleVariableSelect(editor)}
          variablesPluginOptions={variablesPluginOptions}
          insertCleanVariables={insertCleanVariables}
        />
      )}
    </ToolbarContainer>
  );
};

export default RichTextEditorToolbar;
